import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Box, Button, CircularProgress } from "@mui/material";
import { FC, useMemo, useState } from "react";

import { IVehicleLogRowDetails, IVehicleLogViolationDetails } from ".";
import { useAppSelector } from "../../redux";
import { useGetAlertEventsQuery } from "../../redux/reducers/alerts.reducer";
import { useGetAllCamerasListQuery, useGetVehicleTypesListQuery } from "../../redux/reducers/settings.reducer";
import { EVENT_PRODUCT_TYPE_E, IGetAlertEventsRequestData } from "../../types";
import { convertSnakeToTitleCase } from "../../utils/commonUtils";
import VehicleLogContent from "./VehicleLogContent";

interface IFullscreenVehicleDetailsProps {
	sessionId: number;
	navigateBack?: () => void;
}

const FullscreenVehicleDetails: FC<IFullscreenVehicleDetailsProps> = (props) => {
	const { sessionId, navigateBack } = props;

	const authState = useAppSelector((state) => state.auth);

	const [filters, setFilters] = useState<IGetAlertEventsRequestData>({
		page: 1,
		session: sessionId,
		page_size: 1,
		product_type: EVENT_PRODUCT_TYPE_E.RECON
	});

	// APIS
	// GET VEHICLE TYPES LIST
	const { data: vehicleTypesList } = useGetVehicleTypesListQuery(undefined, {
		skip: !authState.user.token || authState.user.token === "invalid"
	});

	// GET CAMERAS LIST
	const { data: camerasListResponse } = useGetAllCamerasListQuery(
		{ product_type: EVENT_PRODUCT_TYPE_E.RECON },
		{ skip: !authState.user.token || authState.user.token === "invalid" }
	);

	// GET ALERT EVENTS
	const {
		data: getVehicleLogsResponse,
		isLoading: getVehicleLogsLoading,
		isFetching: getVehicleLogsFetching,
		refetch: refetchVehicleLogs
	} = useGetAlertEventsQuery(filters, {
		skip: !authState.user.token || authState.user.token === "invalid"
	});

	// CAMERAS LIST
	const camerasList = useMemo(() => camerasListResponse ?? [], [camerasListResponse]);

	const vehicleLogDetails = useMemo<IVehicleLogRowDetails | null>(() => {
		if (!getVehicleLogsResponse || getVehicleLogsResponse.results.length <= 0) return null;

		const vehicleLogItem = getVehicleLogsResponse.results[0];

		const vehicleType = vehicleTypesList?.find((vehicleTypeItem) => vehicleTypeItem.id === vehicleLogItem.vehicle_type);

		const cameraSite = camerasList?.find((cameraItem) => cameraItem.id === vehicleLogItem.camera.id);

		const violations: IVehicleLogViolationDetails[] = [];

		for (const violationItem of vehicleLogItem.violations.filter((item) => !!item.violation_name)) {
			if (typeof violationItem.id === "number" && violationItem.violation_name) {
				violations.push({
					id: violationItem.id ?? 0,
					violation_id: violationItem.violation ?? 0,
					name: convertSnakeToTitleCase(violationItem.violation_name),
					lane_id: violationItem.lane,
					lane: violationItem.lane_name,
					is_speed_related: violationItem.violation_name.toLowerCase().includes("speed")
				});
			}
		}

		const isChallanCreated = vehicleLogItem.challan_info?.challans?.length > 0 || false;
		const retryChallan = vehicleLogItem.challan_info?.challans?.some((item) => item.status === "failed") || false;

		return {
			id: vehicleLogItem.id,
			timestamp: vehicleLogItem.timestamp,
			number_plate: vehicleLogItem.number_plate,
			location: "",
			speed_radar: vehicleLogItem.radar_speed ? `${vehicleLogItem.radar_speed} km/hr` : "",
			speed_vision: vehicleLogItem.speed ? `${vehicleLogItem.speed} km/hr` : "",
			plate_type: vehicleLogItem.vehicle_category ?? "",
			color: vehicleLogItem.color,
			color_icon: vehicleLogItem.color_icon,
			make: vehicleLogItem.vehicle_brand ?? "",
			make_icon: vehicleLogItem.vehicle_brand_icon ?? "",
			camera: vehicleLogItem.site_name,
			class: vehicleLogItem.vehicle_class ?? "",
			device: vehicleLogItem.device.device_name,
			violations: violations,
			event_id: vehicleLogItem.unique_id,
			vehicle_type: vehicleLogItem.vehicle_type,
			vehicle_type_icon: vehicleType?.icon ?? "",
			vehicle_class_icon: vehicleLogItem.vehicle_class_icon ?? "",
			direction: vehicleLogItem.direction,
			direction_name: vehicleLogItem.direction_name ?? "",
			model: vehicleLogItem.vehicle_model ?? "",
			is_bookmarked: vehicleLogItem.is_bookmarked,
			ai_meta_data: vehicleLogItem.ai_meta_data ?? "",
			bbox: vehicleLogItem.bbox ?? [],
			icon: vehicleType?.icon ?? vehicleType?.icon_id ?? null,
			vehicle_type_name: vehicleType?.name ?? "",
			vehicle_number: vehicleLogItem.number_plate,
			plate_image_uuid: vehicleLogItem.plate_evidences?.[0] || "",
			vehicle_images: vehicleLogItem?.new_evidence_images ?? [],
			vehicle_image_frame_id: vehicleLogItem.unique_id,
			site_id: Number(vehicleLogItem.camera.site_id),
			site_name: cameraSite?.site_name ?? "",
			brand_id: Number(vehicleLogItem.vehicle_brand) || 0,
			brand: vehicleLogItem.vehicle_brand ?? "",
			brand_icon: vehicleLogItem.vehicle_brand_icon ?? "",
			color_id: Number(vehicleLogItem.color) || 0,
			speed: vehicleLogItem.speed ? `${vehicleLogItem.speed} km/hr` : "",
			radar_speed: vehicleLogItem.radar_speed ? `${vehicleLogItem.radar_speed} km/hr` : "",
			vehicle_class_id: Number(vehicleLogItem.vehicle_class) || 0,
			vehicle_class: vehicleLogItem.vehicle_class ?? "",
			model_id: Number(vehicleLogItem.vehicle_model) || 0,
			category_id: 0,
			category: vehicleLogItem.vehicle_category ?? "",
			crop_coordinates: vehicleLogItem.crop_co_ordinates
				? {
						plate: vehicleLogItem.crop_co_ordinates.plate,
						violation: vehicleLogItem.crop_co_ordinates.violation?.[0] ?? [],
						...(vehicleLogItem.crop_co_ordinates.plate_image_name != null && {
							plate_image_name: vehicleLogItem.crop_co_ordinates.plate_image_name
						}),
						...(vehicleLogItem.crop_co_ordinates.violation_image_name != null && {
							violation_image_name: vehicleLogItem.crop_co_ordinates.violation_image_name
						})
				  }
				: { plate: [], violation: [] },
			location_coordinates: vehicleLogItem.location_co_ordinates,
			location_name: vehicleLogItem.location_name,
			officer: vehicleLogItem.officer_name,
			violation_image: vehicleLogItem.violation_evidences?.[0] ?? "",
			status: vehicleLogItem.validated_status,
			lanes: vehicleLogItem.camera.camera_config_roi.map((laneItem) => ({ id: laneItem.id, name: laneItem.name })),
			image_width: vehicleLogItem.camera.width,
			image_height: vehicleLogItem.camera.height,
			remarks: vehicleLogItem.comment || "",
			is_challan_created: isChallanCreated,
			retry_challan: retryChallan,
			challan_status: isChallanCreated ? (retryChallan ? "failed" : "issued") : "pending"
		};
	}, [camerasList, getVehicleLogsResponse, vehicleTypesList]);

	function handlePrevious() {
		if (getVehicleLogsResponse && getVehicleLogsResponse.previous) {
			setFilters((currentFilters) => ({ ...currentFilters, page: (currentFilters.page ?? 1) - 1 }));
		}
	}

	function handleNext() {
		if (getVehicleLogsResponse && getVehicleLogsResponse.next) {
			setFilters((currentFilters) => ({ ...currentFilters, page: (currentFilters.page ?? 1) + 1 }));
		}
	}

	function handleUpdated() {
		refetchVehicleLogs();
	}

	// useEffect(() => {
	// 	if (filtersState.timeFilter) {
	// 		setFilters((currentFilters) => ({
	// 			...currentFilters,
	// 			page: 1,
	// 			timestamp__gte: filtersState.timeFilter.start_time,
	// 			timestamp__lte: filtersState.timeFilter.end_time
	// 		}));
	// 	}
	// }, [filtersState]);

	return (
		<Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: "1rem", height: 0 }}>
			<Box className="vehicle-logs-section-header-wrapper">
				<Button variant="text" startIcon={<ArrowBackIcon />} onClick={navigateBack} sx={{ textTransform: "none" }}>
					Back to Sessions List
				</Button>
			</Box>

			<Box className="vehicle-logs-table-wrapper" sx={{ padding: "1rem" }}>
				{getVehicleLogsLoading || getVehicleLogsFetching ? (
					<Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
						<CircularProgress size={32} />
					</Box>
				) : (
					<VehicleLogContent
						vehicleLogDetails={vehicleLogDetails}
						previousButtonDisabled={!getVehicleLogsResponse || !getVehicleLogsResponse.previous}
						nextButtonDisabled={!getVehicleLogsResponse || !getVehicleLogsResponse.next}
						onPrevious={handlePrevious}
						onNext={handleNext}
						onUpdated={() => {}}
						onChallanGenerated={handleUpdated}
					/>
				)}
			</Box>
		</Box>
	);
};

export default FullscreenVehicleDetails;
